import { useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PublicIcon from '@mui/icons-material/Public';

import { useTranslation } from '../../context/TranslationContext.jsx';
import UKFlag from '../../assets/img/countries/united_kingdom_flag.svg';
import NetherlandsFlag from '../../assets/img/countries/dutch_flag.svg';
import { StyledSelect, StyledMenuItem, StyledFlag } from '../../constants/styledComponents';

import {
  HeaderPillButton,
  HeaderControlRow,
  HeaderRowLabelGroup,
  HeaderRowLabel,
  headerRowIconSx,
  LanguagePillButton,
  LanguageMenu,
  LanguageMenuItem,
  FlagSlot,
} from './styles';

const languageOptions = [
  {
    value: 'en',
    code: 'EN',
    labelKey: 'header.languageEnglish',
    flag: UKFlag,
    flagAltKey: 'header.unitedKingdomFlagAlt',
  },
  {
    value: 'nl',
    code: 'NL',
    labelKey: 'header.languageDutch',
    flag: NetherlandsFlag,
    flagAltKey: 'header.netherlandsFlagAlt',
  },
];

const LanguageSelector = ({ variant = 'select', sx }) => {
  const { changeLanguage, language, translate } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const isOpen = Boolean(anchorEl);
  const closeMenu = () => setAnchorEl(null);
  const selectedLanguage =
    languageOptions.find((option) => option.value === language) ?? languageOptions[0];

  const renderLanguageMenu = () => (
    <LanguageMenu
      id="language-selector-menu"
      anchorEl={anchorEl}
      open={isOpen}
      onClose={closeMenu}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      {languageOptions.map((option) => (
        <LanguageMenuItem
          key={option.value}
          selected={option.value === language}
          aria-label={translate(option.labelKey)}
          onClick={() => {
            changeLanguage(option.value);
            closeMenu();
          }}
        >
          <FlagSlot>
            <StyledFlag src={option.flag} alt={translate(option.flagAltKey)} />
          </FlagSlot>
          {option.code}
        </LanguageMenuItem>
      ))}
    </LanguageMenu>
  );

  if (variant === 'row') {
    return (
      <HeaderControlRow sx={sx}>
        <HeaderRowLabelGroup>
          <PublicIcon sx={headerRowIconSx} />
          <HeaderRowLabel>{translate('header.Language')}</HeaderRowLabel>
        </HeaderRowLabelGroup>
        <LanguagePillButton
          type="button"
          aria-label={translate('header.languageSelector')}
          aria-controls={isOpen ? 'language-selector-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={isOpen ? 'true' : undefined}
          onClick={(event) => setAnchorEl(event.currentTarget)}
          endIcon={<KeyboardArrowDownIcon />}
        >
          {selectedLanguage.code}
        </LanguagePillButton>
        {renderLanguageMenu()}
      </HeaderControlRow>
    );
  }

  if (variant === 'pill') {
    return (
      <>
        <HeaderPillButton
          type="button"
          aria-label={translate('header.languageSelector')}
          aria-controls={isOpen ? 'language-selector-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={isOpen ? 'true' : undefined}
          onClick={(event) => setAnchorEl(event.currentTarget)}
          startIcon={<PublicIcon />}
          sx={sx}
        >
          {selectedLanguage.code}
        </HeaderPillButton>
        {renderLanguageMenu()}
      </>
    );
  }

  return (
    <StyledSelect
      value={language}
      onChange={(e) => changeLanguage(e.target.value)}
      displayEmpty
      inputProps={{ 'aria-label': translate('header.languageSelector') }}
    >
      {languageOptions.map((option) => (
        <StyledMenuItem
          key={option.value}
          value={option.value}
          aria-label={translate(option.labelKey)}
        >
          <StyledFlag src={option.flag} alt={translate(option.flagAltKey)} />
          {option.code}
        </StyledMenuItem>
      ))}
    </StyledSelect>
  );
};

export default LanguageSelector;
